import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { RdContextType } from "../@types/Rd";
import { IMsalContext, useIsAuthenticated, useMsal } from "@azure/msal-react";
import SsoService, { ISsoService } from "../services/SsoService";
import UserModel, { IUserModel } from "../models/DTO/UserModel";
import { IUIContext } from "../models/Context/UIContextModel"
import { AccountInfo, InteractionType } from "@azure/msal-browser";
import { RouterProvider } from "react-router-dom";
import { GetBrowserRouter } from "../configurations/RoutesConfig";
import CompanyModel, { ICompanyModel } from "../models/DTO/CompanyModel";
import UserLoginModel, { IUserLoginModel } from "../models/DTO/UserLoginModel";
import { UserLoginByCompany } from "../services/UserService";
import { DecryptContext, EncryptContext, ProtectKeyContext } from "../Helpers/CryptoHelper";
import { IErrorModel } from "../models/DTO/ErrorModel";
import MenuModel, { IMenuModel } from "../models/DTO/MenuModel";
import UserCompanyPermissionModel, { IUserCompanyPermissionModel } from "../models/DTO/UserCompanyPermissionModel";
import { GetUserCompanyPermissions } from "../services/UserCompanyPermissionService";
import axios, { CancelTokenSource } from "axios";
import { ApiErrorProvider } from "./ApiErrorProvider";
import _ from "lodash";
import AggregateRequestHandle, { AggregateResponseHandle, RequestAggregateHandleType } from "../Helpers/AxiosRequestAggregateHelper";
import { HandledResponseType } from "../services/HttpService";

type LoginByCompanyArgs = {
    company: ICompanyModel | undefined,
    user: AccountInfo | null,
    isMasterActive: boolean
}

const SerializePermisionKey: string = "PermissionCache"

const DefaultContext: RdContextType = {
    AdToken: undefined,
    AdAuthProgress: undefined,
    ChangeMasterActivity: (activity: boolean) => { },
    Companies: [],
    currentCompany: undefined,
    GetProfileData: (): void => { },
    isAdAuthenticated: false,
    IsMasterActive: false,
    inLogingProgress: false,
    Login: (type: InteractionType): void => { },
    Logout: (type: InteractionType): void => { },
    Permissions: new Map(),
    UpdateAdAccountInfo: (accountInfo: AccountInfo | null) => { },
    UpdateAdToken: (token?: string) => { },
    UpdatePermissions: () => { },
    RdToken: undefined,
    RedirectLogin: (companyParamValue: string): void => { },
    ReloadPermissions: (force?: boolean): void => { },
    ResetMasterActivity: () => { },
    UI: undefined,
    userInfo: undefined,
    userMenus: [],
}

const RdContext = React.createContext<RdContextType>(DefaultContext);

const GetProfileDataHandle = (service: ISsoService, setUserInfo: Dispatch<SetStateAction<IUserModel | undefined>>): void => service.GetProfileData((response) => setUserInfo(response.data));

const LoginHandle = (service: ISsoService, type: InteractionType): void => service!.Login(type);

const LogoutHandle = (service: ISsoService, type: InteractionType, cachedContextName: string): void =>
    service?.Logout != null && service.Logout(type, () => RemoveContext(cachedContextName)) || RemoveContext(cachedContextName);

const LoadContextFromSession = (name: string, ssoService: ISsoService, setUserInfo: Dispatch<SetStateAction<IUserModel | undefined>>): { Context: RdContextType, Permissions: [string, IUserCompanyPermissionModel][] } => {
    const cached: string | null = sessionStorage.getItem(ProtectKeyContext(name));

    if (cached != null) {
        const data: string = DecryptContext(name, cached),
            obj = JSON.parse(data),
            context: RdContextType = {
                ...obj,
                GetProfileData: () => GetProfileDataHandle(ssoService, setUserInfo),
                Login: (type) => LoginHandle(ssoService, type),
                Logout: (type) => LogoutHandle(ssoService, type, name),
            };
        return { Context: context, Permissions: obj[SerializePermisionKey] || [] };
    }
    return { Context: DefaultContext, Permissions: [] };
}

const SaveContext = (name: string, context: RdContextType): void => {
    const cached: string | null = sessionStorage.getItem(name);
    if (cached)
        RemoveContext(name);

    const permissionFromMapToArray = Array.from(context.Permissions.entries());
    const newObj = { ...context, [SerializePermisionKey]: permissionFromMapToArray };

    const cache: string = EncryptContext(name, newObj);
    sessionStorage.setItem(ProtectKeyContext(name), cache)
}

const RemoveContext = (name: string): void => sessionStorage.removeItem(ProtectKeyContext(name));


type LoadContextParams = {
    isLoadingContext: boolean,
    SSOService: ISsoService,

    setCurrentCompany: Dispatch<SetStateAction<ICompanyModel | undefined>>,
    setRdToken: Dispatch<SetStateAction<string | undefined>>,
    setUiInfo: Dispatch<SetStateAction<IUIContext | undefined>>,
    setUserInfo: Dispatch<SetStateAction<IUserModel | undefined>>,
    setCompanyPermissionDict: Dispatch<SetStateAction<Map<string, IUserCompanyPermissionModel>>>,
    reloadPermissions: (force?: boolean, givenToken?: string) => void,
    setIsLoadContext: Dispatch<SetStateAction<boolean>>,
    setIsMasterActive: Dispatch<SetStateAction<boolean>>
}

const LoadContext = ({ isLoadingContext, SSOService, setCurrentCompany, setRdToken, setUiInfo, setUserInfo, setCompanyPermissionDict, reloadPermissions, setIsLoadContext, setIsMasterActive }: LoadContextParams): void => {
    const accInfo: AccountInfo | null = SSOService.GetAccountInfo();

    if (isLoadingContext && accInfo != null) {
        const { Context: cachedContext, Permissions } = LoadContextFromSession(accInfo!.localAccountId!, SSOService!, setUserInfo);

        if (cachedContext != DefaultContext) {

            if (cachedContext.currentCompany != null) {
                const pickedCompany: ICompanyModel = new CompanyModel().LoadFromCached(cachedContext.currentCompany);
                setCurrentCompany(pickedCompany);
            }

            cachedContext.RdToken != null && setRdToken(cachedContext.RdToken);
            cachedContext.UI != null && Object.values(cachedContext.UI).every(x => x != null && x != "") && setUiInfo(cachedContext.UI);
            setIsMasterActive(cachedContext.IsMasterActive);

            if (cachedContext.userInfo != null) {
                const cachedUser: IUserModel = new UserModel().LoadFromCached(cachedContext.userInfo);
                setUserInfo(cachedUser);
            }

            if (Permissions != null && Permissions.some(x => x)) {
                const cached = Permissions.map(([key, value]) => {
                    const newValue = new UserCompanyPermissionModel().LoadFromCached(value),
                        result: [string, IUserCompanyPermissionModel] = [key, newValue];
                    return result;
                }),
                    dict = new Map<string, IUserCompanyPermissionModel>(cached);
                setCompanyPermissionDict(dict);
            }
            else
                reloadPermissions();
        }
    }
    setIsLoadContext(false);
}

export const RdProvider = ({ children }: { children?: JSX.Element }): JSX.Element => {
    const adContext: IMsalContext = useMsal(),
        [SSOService, setSSOService] = useState<ISsoService>(new SsoService(adContext)),
        isAdAuthenticated = useIsAuthenticated(),
        [companies, setCompanies] = useState<ICompanyModel[]>([]),
        [userMenus, setUserMenus] = useState<IMenuModel[]>([]),
        [currentCompany, setCurrentCompany] = useState<ICompanyModel | undefined>(),
        [userInfo, setUserInfo] = useState<IUserModel | undefined>(),
        [adToken, setAdToken] = useState<string | undefined>(undefined),
        [accInfo, setAccInfo] = useState<AccountInfo | null>(null),
        [uiInfo, setUiInfo] = useState<IUIContext | undefined>({ Email: accInfo?.username, Name: accInfo?.name }),
        [rdToken, setRdToken] = useState<string | undefined>(undefined),
        [apiError, setApiError] = useState<IErrorModel | undefined>(),
        [ctx, setCtx] = useState<RdContextType>(DefaultContext),
        [isLoadingContext, setIsLoadContext] = useState<boolean>(ctx == DefaultContext),
        [companyPermissionDict, setCompanyPermissionDict] = useState<Map<string, IUserCompanyPermissionModel>>(new Map()),
        router = GetBrowserRouter(),
        [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | undefined>(axios.CancelToken.source()),
        [reloadPermissionsInProgress, setReloadPermissionsInProgress] = useState<boolean>(false),
        [inLogingProgress, setInLoginProgress] = useState<boolean>(false),
        [isMasterActive, setIsMasterActive] = useState<boolean>(false),
        loadData = (token: string): void => {
            const requests: RequestAggregateHandleType[] = [
                {
                    Endpoint: base => base.Company!.GetAll!,
                    Verb: "GET",
                    ResponseHandle<ICompanyModel>(response: HandledResponseType) {
                        const { Data: data } = response,
                            companies: ICompanyModel[] = (data && data.map((item: any) => new CompanyModel().LoadFromAPI(item))) || [];

                        return companies;
                    },
                },
                {
                    Endpoint: base => base.Menu!.GetTreeViewMenu!,
                    Verb: "GET",
                    ResponseHandle<IMenuModel>(response: HandledResponseType) {
                        const { Data: data } = response,
                            menuItems: IMenuModel[] = (data && data.map((item: any) => new MenuModel().LoadFromAPI(item))) || [];

                        return menuItems;
                    }
                }
            ];

            AggregateRequestHandle(token, requests,
                (responses: HandledResponseType[]) => {
                    const //{ Messages: lstMessages } = AggregateResponseWarningOrErrorHandle(responses),
                        [companiesResponse, menuItemsResponse] = responses;

                    let skip = AggregateResponseHandle<Array<ICompanyModel>>(companiesResponse as HandledResponseType<Array<ICompanyModel>>, setApiError,
                        (companies) => {
                            if (companies != null && companies.some(x => x)) {
                                const filtered = companies.filter(x => x != null && x.IsActive),
                                    ordered = _.orderBy(filtered, c => c.Name);

                                setCompanies(ordered);
                            }
                        })


                    if (!skip) {
                        skip = AggregateResponseHandle<Array<IMenuModel>>(menuItemsResponse as HandledResponseType<Array<IMenuModel>>, setApiError,
                            (menus) => {
                                if (menus != null && menus.some(x => x)) {
                                    const filtered = menus
                                        .filter(x => x != null);

                                    setUserMenus(filtered);
                                }
                            })
                    }

                    // HandleDefaultDialogs(dialogSet, lstMessages);

                }, cancelTokenSource?.token);
        },
        reloadPermissions: (force?: boolean, givenToken?: string) => void = (force = false, givenToken = undefined) => {
            const token: string | undefined = (givenToken != null && givenToken != "" && givenToken!) || (rdToken != null && rdToken != "" && rdToken) || undefined,
                inProgress: boolean = !force && reloadPermissionsInProgress;

            if ((force || apiError == null) && token != null && token != "" && !inProgress && (force || companyPermissionDict == null || companyPermissionDict.size <= 0)) {
                setReloadPermissionsInProgress(true);
                GetUserCompanyPermissions(token!, (permissions, error) => {
                    if (error != null)
                        setApiError(error);

                    if (error == null) {
                        const filtered = permissions.filter(p => p != null && p.Id > 0 && p.UserId > 0 && p.CompanyId > 0);

                        if (filtered != null && filtered.some(x => x)) {
                            //must have only one premission in key "user|company";
                            const dict = filtered
                                .reduce((map, permission) => {
                                    const key: string = `${permission.UserId}|${permission.CompanyId}`, //check user form when change this.
                                        result = map.set(key, permission);

                                    return result;
                                }, new Map<string, IUserCompanyPermissionModel>());

                            setCompanyPermissionDict(dict);

                            if (isLoadingContext)
                                setIsLoadContext(false);
                        }
                    }

                    setInLoginProgress(false);
                    setReloadPermissionsInProgress(false);
                }, cancelTokenSource?.token);
            }
        },
        LoginByCompany = ({ company, user, isMasterActive, ...rest }: LoginByCompanyArgs): void => {
            if (company != null && user != null) {
                setInLoginProgress(true);
                const userlogin: IUserLoginModel = new UserLoginModel(user.username, company.Id, isMasterActive);
                UserLoginByCompany(userlogin, (userModel: IUserModel | undefined, error: IErrorModel | undefined) => {

                    if (error != null) {

                        if (error.ErrorCode != null && (error.ErrorCode == 401 || error.ErrorCode == 403)) {
                            setIsMasterActive(false);
                            setRdToken("");
                        }

                        setApiError(error);
                    }

                    if (userModel != null) {
                        const token = userModel.GetToken();
                        setApiError(undefined);
                        setUserInfo(userModel);
                        setRdToken(token);
                        reloadPermissions(true, token);
                    }

                    setInLoginProgress(false);
                }, cancelTokenSource?.token);
            }
        },
        redirectLogin = (companyParamValue: string): void => {
            if (companies != null && companies.some(x => x.Id > 0) && accInfo != null) {
                const identifier: number = Number.parseInt(companyParamValue),
                    filtered = companies.find(c => c.Id == identifier);

                if (filtered != null)
                    setCurrentCompany(filtered);
            }
        };

    useEffect(() => {

        LoadContext({ isLoadingContext, SSOService, setCurrentCompany, setRdToken, setUiInfo, setUserInfo, setCompanyPermissionDict, reloadPermissions, setIsLoadContext, setIsMasterActive });

        if (apiError == null) {
            loadData(rdToken || "");
        }

        return () => cancelTokenSource?.cancel();

    }, []);

    useEffect(() => {
        !isLoadingContext && (rdToken == null || rdToken == "") && LoginByCompany({ company: currentCompany, user: accInfo, isMasterActive });
    }, [currentCompany, isMasterActive]);

    useEffect(() => {
        const context: RdContextType = {
            AdAuthProgress: adContext.inProgress,
            AdToken: adToken,
            ChangeMasterActivity: (activity: boolean) => { setRdToken(""); setIsMasterActive(activity); },
            Companies: companies,
            currentCompany,
            GetProfileData: () => GetProfileDataHandle(SSOService, setUserInfo),
            isAdAuthenticated: isAdAuthenticated,
            IsMasterActive: isMasterActive,
            inLogingProgress: inLogingProgress,
            Login: (type) => LoginHandle(SSOService, type),
            Logout: (type) => LogoutHandle(SSOService, type, accInfo!.localAccountId),
            Permissions: companyPermissionDict,
            UpdatePermissions: setCompanyPermissionDict,
            RedirectLogin: redirectLogin,
            ReloadPermissions: reloadPermissions,
            ResetMasterActivity: () => setIsMasterActive(false),
            RdToken: rdToken,
            UI: { Name: accInfo?.name, Email: accInfo?.username },
            UpdateAdAccountInfo: (accountInfo) => setAccInfo(accountInfo),
            UpdateAdToken: (token) => setAdToken(token),
            UpdateCurrentCompany: setCurrentCompany,
            UpdateRdToken: setRdToken,
            UpdateUserInfo: setUserInfo,
            userInfo,
            userMenus
        };

        setCtx(prev => {
            return { ...prev, ...context }
        });

        if (companies && companies.length == 1 && currentCompany == null)
            setCurrentCompany(companies[0]);

        if (isAdAuthenticated && !isLoadingContext && [currentCompany, userInfo, rdToken, accInfo].every(x => x != null)) {
            SaveContext(accInfo!.localAccountId, context);
        }

    }, [companies, currentCompany, isAdAuthenticated, adToken, rdToken, inLogingProgress, userInfo, userMenus, companyPermissionDict, isMasterActive]);

    return (
        <RdContext.Provider value={ctx}>
            <ApiErrorProvider cancelTokenSource={axios.CancelToken.source()} error={apiError}>
                <RouterProvider router={router} />
                {children}
            </ApiErrorProvider>
        </RdContext.Provider>
    )
}

export default RdContext;